import React, { createContext, useContext, useState, useEffect } from 'react';
import { authenticatedFetch } from '../utils/api';
import { Navigate } from 'react-router-dom';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [planLimits, setPlanLimits] = useState(null);
    const [assessmentCompleted, setAssessmentCompleted] = useState(null);
    const [currentStreak, setCurrentStreak] = useState(0);

    useEffect(() => {
        const token = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        if (token && storedUser) {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            setAssessmentCompleted(parsedUser.assessment_completed);
            setCurrentStreak(parsedUser.current_streak || 0);
            fetchPlanLimits();
        }
        setLoading(false);
    }, []);

    const fetchPlanLimits = async () => {
        try {
            const data = await authenticatedFetch('/api/auth/limits');

            // Update plan limits without triggering a full user state update
            setPlanLimits(data);

            // Update local storage without triggering state updates
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                const parsedUser = JSON.parse(storedUser);
                const updatedUser = {
                    ...parsedUser,
                    plan: data
                };
                localStorage.setItem('user', JSON.stringify(updatedUser));

                // Update user state only if it has changed
                setUser(prev => {
                    if (JSON.stringify(prev?.plan) !== JSON.stringify(data)) {
                        return updatedUser;
                    }
                    return prev;
                });
            }

            return data;
        } catch (error) {
            console.error('Error fetching plan limits:', error);
            return null;
        }
    };

    const updateUserPlan = async () => {
        return await fetchPlanLimits();
    };

    const handleAuthSuccess = (token, userData) => {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        setAssessmentCompleted(userData.assessment_completed);
        setCurrentStreak(userData.current_streak || 0);
        fetchPlanLimits();
    };

    const login = async (credentials) => {
        try {
            const data = await authenticatedFetch('/api/auth/login', {
                method: 'POST',
                body: credentials
            });

            handleAuthSuccess(data.token, data.user);
        } catch (error) {
            throw new Error(error.message);
        }
    };

    const register = async (userData) => {
        try {
            // eslint-disable-next-line
            const data = await authenticatedFetch('/api/auth/register', {
                method: 'POST',
                body: userData
            });

            return <Navigate to="/login" />;
        } catch (error) {
            throw new Error(error.message);
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
    };

    if (loading) {
        return null;
    }

    const updateUser = (userData) => {
        setUser(userData);
    };

    return (
        <AuthContext.Provider value={{
            user,
            planLimits,
            currentStreak,
            login,
            logout,
            register,
            updateUserPlan,
            handleAuthSuccess,
            fetchPlanLimits,
            assessmentCompleted,
            setAssessmentCompleted,
            setCurrentStreak,
            updateUser
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);